import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { AuthProvider } from './auth/clerkAuth';
import App from './App';
import { initNative } from './native/capacitor';
import 'material-symbols/outlined.css';
import '@fontsource-variable/inter';
import '@fontsource-variable/manrope';
import './index.css';
import { ensureFontsReady } from './lib/fonts';

// Initialize native bridge (deep links, push, status bar).
// On web this is a no-op; on native it wires Capacitor listeners.
void initNative((url) => {
  // Route captured URLs into the existing shared-url capture flow via hash param
  window.location.hash = `#/?shared=true&url=${encodeURIComponent(url)}`;
});

// Configure TanStack Query client with default options
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes before considering it stale
      staleTime: 1000 * 60 * 5, // 5 minutes
      
      // Keep unused data in cache for 30 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      
      // Retry failed requests with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch on window focus and reconnect
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      
      // Don't refetch on mount if data is fresh
      refetchOnMount: false,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      retryDelay: 1000,
      
      // Enable network mode for offline queueing
      // Mutations will be paused when offline and resumed when online
      networkMode: 'offlineFirst',
    },
  },
});

// Resume paused mutations when connection is restored
// Validates: Requirements 23.4
window.addEventListener('online', () => {
  queryClient.resumePausedMutations();
});

// Register service worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('[PWA] Service Worker registered:', registration.scope);
        
        // Listen for new SW versions — notify the app so it can show an update prompt
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                window.dispatchEvent(new CustomEvent('sw-update-ready'));
              }
            });
          }
        });

        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 1000 * 60 * 60); // Check every hour
      })
      .catch((error) => {
        console.error('[PWA] Service Worker registration failed:', error);
      });
  });
}

ensureFontsReady();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log errors to console for debugging
        // Validates: Requirements 23.6
        console.error('Application error:', error, errorInfo);
      }}
    >
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
